/**
 * BRINGING WHAT A TAP OPENED INTO VIEW, clear of the nav, and moving only as she has asked the device
 * to (round four, slices F1 and M2). The bottom nav is drawn 72 tall at 390 and at 1280 and sits over
 * the foot of the page, so what opens under the last thing she tapped can open under it, out of
 * sight: the replay of the owner's walk found an invoice's "Which area?" and Send there after a tap on
 * Good. The month brings a chosen day into view the same way (`components/Month.tsx`). A screen that
 * lands her on a place, as Pay lands on the ways to pay, brings it to the top instead. Either moves
 * smoothly, unless she has asked the device for less motion, where it moves at once
 * (`prefers-reduced-motion: reduce`). A browser that cannot say is taken to allow it, as CSS takes it.
 * A landing holds its place for a few seconds while the page's own layout settles, until she does
 * anything herself (`bringToTopAndHold`).
 */

/**
 * The scroll margins of what is brought into view: a little room above, and below it 96 for the nav
 * and the phone's own bar at the foot, so its last line stands clear of both. Anything taller than
 * the screen is brought to its top instead (`block: 'nearest'`).
 */
export const CLEAR_OF_THE_NAV = 'scroll-mt-4 scroll-mb-[calc(6rem+env(safe-area-inset-bottom))]';

/** Whether she has asked the device for less motion, where it can say. */
export function lessMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Scrolls the page only as far as it takes to show `element` whole, by its scroll margins: gliding,
 * or in one step for someone who asked for less motion. Nothing on a device that cannot scroll.
 */
export function bringIntoView(element: Element | null): void {
  element?.scrollIntoView?.({ block: 'nearest', behavior: lessMotion() ? 'auto' : 'smooth' });
}

/**
 * Brings `element` to the top of the screen, as far as the page allows, leaving the room its own
 * scroll margin asks above it: smoothly, or at once where she asked for less motion. A browser with
 * no `scrollIntoView` leaves the screen where it is.
 */
export function bringToTop(element: Element | null | undefined): void {
  element?.scrollIntoView?.({ block: 'start', behavior: lessMotion() ? 'auto' : 'smooth' });
}

/**
 * How long a landing holds its place (`bringToTopAndHold`): the page's own layout has settled by
 * then, its read drawn and its type laid out, and anything later is hers to scroll to.
 */
export const HOLD_MS = 3_000;

/**
 * What lets a landing go: anything she does herself, a wheel, a touch, a press or a key, after which
 * the page is hers; and the focus placed on anything but what had it as the page landed, which a
 * screen places for her to read.
 */
const LET_GO_ON = ['wheel', 'touchstart', 'pointerdown', 'keydown', 'focusin'] as const;

/** The boxes `element` stands for in the layout: its children, where it is drawn as if not there. */
function boxesOf(element: Element): Element[] {
  return window.getComputedStyle(element).display === 'contents'
    ? Array.from(element.children).flatMap(boxesOf)
    : [element];
}

/** Every box laid out before `element` in the page, whose size decides where it stands. */
function laidOutAbove(element: Element): Element[] {
  const above: Element[] = [];
  for (
    let at: Element | null = element;
    at !== null && at !== document.body;
    at = at.parentElement
  ) {
    for (
      let before = at.previousElementSibling;
      before !== null;
      before = before.previousElementSibling
    ) {
      above.push(...boxesOf(before));
    }
  }
  return above;
}

/** Where `element` stands from the top of the page, and how long the page is, to the pixel. */
function placeOf(element: Element): { readonly top: number; readonly length: number } {
  return {
    top: Math.round(element.getBoundingClientRect().top + window.scrollY),
    length: document.documentElement.scrollHeight,
  };
}

/**
 * A LANDING THAT HOLDS ITS PLACE (after CI run 39344566493): brings `part` to the top of the screen,
 * as `bringToTop` does, and then, for `HOLD_MS`, brings it there again each time the page's own
 * layout moves it or changes how far the page can scroll: content come in above it, type drawn wider
 * than the screen was first laid out in, the part itself grown. A smooth scroll goes where the part
 * stood as it started, so without this a page still settling could leave the part short of where
 * it was brought, under the nav. Only until she does anything herself, or the focus is placed on
 * something else (`LET_GO_ON`): nothing moves the page after that. The focus the page's heading
 * took as she arrived, which a landing after a move waits for, is not something else, though its
 * `focusin` is heard only once the landing has begun. Where the browser cannot watch the layout,
 * the part is brought to the top once. Gives back the way to let go, for a screen that goes.
 */
export function bringToTopAndHold(part: Element | null | undefined): () => void {
  if (part === null || part === undefined) return () => undefined;
  bringToTop(part);
  if (typeof window.ResizeObserver !== 'function') return () => undefined;

  /** What had the focus as the page landed, whose own `focusin` lets nothing go. */
  const focused = document.activeElement;
  let was = placeOf(part);
  const observer = new ResizeObserver(() => {
    if (!part.isConnected) {
      letGo();
      return;
    }
    const now = placeOf(part);
    if (now.top === was.top && now.length === was.length) return;
    was = now;
    bringToTop(part);
  });
  const holding = setTimeout(letGo, HOLD_MS);
  function letGo() {
    clearTimeout(holding);
    observer.disconnect();
    for (const kind of LET_GO_ON) window.removeEventListener(kind, heard, true);
  }
  function heard(event: Event) {
    if (event.type === 'focusin' && event.target === focused) return;
    letGo();
  }
  for (const box of [document.documentElement, part, ...laidOutAbove(part)]) observer.observe(box);
  for (const kind of LET_GO_ON) {
    window.addEventListener(kind, heard, { capture: true, passive: true });
  }
  return letGo;
}

/** An element's scroll margin on one side, in pixels, or nothing where the browser says none. */
function marginOf(element: Element, side: 'scrollMarginTop' | 'scrollMarginBottom'): number {
  const value = Number.parseFloat(window.getComputedStyle(element)[side]);
  return Number.isFinite(value) ? value : 0;
}

/**
 * A SCREEN AND THE BUTTON IT IS FOR (round six, slice S1): brings `heading` to the top of the screen,
 * as `bringToTop` does, where everything from it down to `action`, with the room each one's scroll
 * margin asks, fits on the screen; and where it does not, as the bank's heading, ops's whole paragraph
 * beside her tick and its Pay do not on a phone, brings `action` into view instead, just clear of the
 * nav (`bringIntoView`, the action carrying `CLEAR_OF_THE_NAV`), so the button a move led her to is
 * never left below the fold. Nothing moves where the browser cannot scroll.
 */
export function bringScreenToTop(heading: Element | null, action: Element | null): void {
  if (heading !== null && action !== null) {
    const from = heading.getBoundingClientRect().top - marginOf(heading, 'scrollMarginTop');
    const to = action.getBoundingClientRect().bottom + marginOf(action, 'scrollMarginBottom');
    if (to - from > window.innerHeight) {
      bringIntoView(action);
      return;
    }
  }
  bringToTop(heading);
}
